// Best-effort scrubbing of values the assistant must not read. Leaf module: it imports nothing, so any
// serializer can use it without creating a cycle.

export const REDACTED = '•••• (redacted)';

const SENSITIVE_AUTOCOMPLETE = new Set(['one-time-code', 'current-password', 'new-password']);

// Matched against a normalized hint padded with single spaces, so `#` and word edges stay exact:
// "routingNumber" and "SSN/ATIN/ITIN/PTIN" match, "Shipping", "Mapping" and a bare "Account" do not.
const SENSITIVE_PATTERNS = [
  / (ssn|itin|atin|ptin|ein|tin) /,
  / social security /,
  / tax(payer)? (id|identification) /,
  / routing /,
  / iban /,
  / (account|acct) (number|num|no|#) /,
  / (card number|credit card|debit card|cvv|cvc|csc|security code) /,
  / (password|passcode|passphrase|pin|otp|verification code|one time (code|password|passcode)) /,
  / (passport|driver( s|s)? licen[sc]e) /,
];

const normalizeHint = (text: string): string => ` ${text
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .toLowerCase()
  .replace(/[^a-z0-9#]+/g, ' ')
  .trim()} `;

export const isSensitiveText = (text: string): boolean => {
  const hint = normalizeHint(text);
  return SENSITIVE_PATTERNS.some((pattern) => pattern.test(hint));
};

// Values chosen from a closed list (selects, comboboxes, checkboxes) are display names, not typed secrets;
// exempting them keeps ledger pickers such as "Bank Account" readable.
const isClosedChoice = (el: Element): boolean => {
  const type = (el.getAttribute('type') ?? '').toLowerCase();
  return type === 'checkbox'
    || type === 'radio'
    || el.tagName === 'SELECT'
    || el.getAttribute('role') === 'combobox'
    || el.getAttribute('aria-haspopup') === 'listbox';
};

// MUI emits random ids (`mui-<n>`, `:r1:`) that say nothing about the field.
const isGeneratedId = (id: string): boolean => /^(mui-|:r)/.test(id);

/**
 * Whether the value of a form field must not be reported. Attribute rules (password type, sensitive
 * `autocomplete` tokens) apply to every field; label/name heuristics apply to typed text only.
 */
export const isSensitiveField = (el: Element, label: string): boolean => {
  if ((el.getAttribute('type') ?? '').toLowerCase() === 'password') return true;

  const tokens = (el.getAttribute('autocomplete') ?? '').toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.some((token) => token.startsWith('cc-') || SENSITIVE_AUTOCOMPLETE.has(token))) return true;

  if (isClosedChoice(el)) return false;

  const id = el.getAttribute('id') ?? '';
  return [label, el.getAttribute('name'), el.getAttribute('aria-label'), el.getAttribute('placeholder'), isGeneratedId(id) ? '' : id]
    .some((hint) => hint != null && hint !== '' && isSensitiveText(hint));
};

const passesLuhn = (digits: string): boolean => {
  const sum = digits.split('').reverse().reduce((total, char, index) => {
    const digit = Number(char);
    if (index % 2 === 0) return total + digit;
    const doubled = digit * 2;
    return total + (doubled > 9 ? doubled - 9 : doubled);
  }, 0);
  return sum % 10 === 0;
};

const SSN_FORMAT = /\b\d{3}-\d{2}-\d{4}\b/g;
const CARD_RUN = /\b\d(?:[ -]?\d){12,18}\b/g;

/**
 * Defense in depth for free text: formatted SSNs and Luhn-valid card numbers. Bare 9-digit runs and
 * `##-#######` are deliberately left alone because they collide with file numbers, ZIP+4 and amounts.
 */
export const redactText = (text: string): string => text
  .replace(SSN_FORMAT, REDACTED)
  .replace(CARD_RUN, (run) => (passesLuhn(run.replace(/\D/g, '')) ? REDACTED : run));
